import { useState, useMemo } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Switch } from '@/components/ui/Switch';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

const WORDS = `lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua enim ad minim veniam quis nostrud exercitation ullamco laboris nisi ex ea commodo consequat duis aute irure in reprehenderit voluptate velit esse cillum eu fugiat nulla pariatur excepteur sint occaecat cupidatat non proident sunt culpa qui officia deserunt mollit anim id est laborum perspiciatis unde omnis natus error voluptatem accusantium doloremque laudantium totam rem aperiam eaque ipsa quae ab illo inventore veritatis quasi architecto beatae vitae dicta explicabo nemo voluptas aspernatur aut odit fugit sequi quia magni dolores eos ratione sequi nesciunt`
  .trim()
  .split(/\s+/);

function rand(n: number): number {
  return Math.floor(Math.random() * n);
}

function genSentence(min = 5, max = 15): string {
  const len = min + rand(max - min + 1);
  const words: string[] = [];
  for (let i = 0; i < len; i++) words.push(WORDS[rand(WORDS.length)]);
  words[0] = words[0][0].toUpperCase() + words[0].slice(1);
  return words.join(' ') + '.';
}

function genParagraph(min = 4, max = 8): string {
  const count = min + rand(max - min + 1);
  const out: string[] = [];
  for (let i = 0; i < count; i++) out.push(genSentence());
  return out.join(' ');
}

type Mode = 'paragraphs' | 'sentences' | 'words';

export function LoremIpsum() {
  const [mode, setMode] = useState<Mode>('paragraphs');
  const [count, setCount] = useState(3);
  const [startsWithLorem, setStartsWithLorem] = useState(true);
  const [seed, setSeed] = useState(0);

  const text = useMemo(() => {
    let out = '';
    if (mode === 'paragraphs') {
      const ps: string[] = [];
      for (let i = 0; i < count; i++) {
        let p = genParagraph();
        if (i === 0 && startsWithLorem) {
          p = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. ' + p;
        }
        ps.push(p);
      }
      out = ps.join('\n\n');
    } else if (mode === 'sentences') {
      const arr: string[] = [];
      for (let i = 0; i < count; i++) arr.push(genSentence());
      if (startsWithLorem)
        arr[0] = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.';
      out = arr.join(' ');
    } else {
      const arr: string[] = [];
      for (let i = 0; i < count; i++) arr.push(WORDS[rand(WORDS.length)]);
      if (startsWithLorem)
        arr.splice(0, 4, 'Lorem', 'ipsum', 'dolor', 'sit');
      out = arr.join(' ') + '.';
      out = out[0].toUpperCase() + out.slice(1);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, count, startsWithLorem, seed]);

  return (
    <div>
      <ToolHeader
        toolId="lorem-ipsum"
        title="Lorem ipsum generator"
        description="Generate placeholder text by paragraphs, sentences, or words."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-4">
          <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
            <TabsList className="w-full">
              <TabsTrigger value="paragraphs" className="flex-1">
                Paragraphs
              </TabsTrigger>
              <TabsTrigger value="sentences" className="flex-1">
                Sentences
              </TabsTrigger>
              <TabsTrigger value="words" className="flex-1">
                Words
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Field label="Count">
            <Input
              type="number"
              value={count}
              onChange={(e) =>
                setCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))
              }
              min={1}
              max={50}
            />
          </Field>
          <label className="flex items-center justify-between text-sm cursor-pointer">
            <span className="text-fg-muted">Start with "Lorem ipsum"</span>
            <Switch checked={startsWithLorem} onChange={setStartsWithLorem} />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" size="sm" onClick={() => setSeed((s) => s + 1)}>
              <RefreshCw size={11} />
              Reroll
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                const ok = await copyToClipboard(text);
                if (ok) toast.success('Copied');
              }}
            >
              <Copy size={11} />
              Copy
            </Button>
          </div>
        </Card>

        <Card className="lg:col-span-2 lg:order-1 !p-0">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            Output
          </div>
          <div className="p-5 text-sm leading-relaxed whitespace-pre-wrap">{text}</div>
        </Card>
      </div>
    </div>
  );
}

export default LoremIpsum;
